import React, { useEffect, useState } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';

const FreelancerWithdrawalBankDetailsPage = () => {
  const [bankDetails, setBankDetails] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');

  const apiBaseURL = process.env.REACT_APP_API_BASE_URL;

  const columns = [
    'Bank Detail ID',
    'Freelancer ID',
    'Bank Name',
    'Account Number',
    'Account Holder Name',
    'IFSC Code',
    'Account Type',
    'Status',
    'Cashfree Beneficiary ID',
    'Cashfree Name',
    'Cashfree Branch',
    'Cashfree City',
    'Created At',
    'Updated At'
  ];

  useEffect(() => {
    const fetchBankDetails = async () => {
      const token = localStorage.getItem('employeeToken');
      if (!token) {
        window.location.href = "/employee-login";
        return;
      }

      try {
        const response = await axios.get(`${apiBaseURL}/api/freelancer-withdrawal-bank-details`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
        setBankDetails(response.data);
        setFilteredData(response.data);
      } catch (err) {
        setError('Failed to fetch bank details.');
      } finally {
        setLoading(false);
      }
    };

    fetchBankDetails();
  }, [apiBaseURL]);

  const handleDateFilter = () => {
    if (!fromDate || !toDate) {
      setFilteredData(bankDetails);
      return;
    }

    const from = new Date(fromDate);
    const to = new Date(toDate);
    to.setHours(23, 59, 59, 999); // include full 'to' day

    const filtered = bankDetails.filter(detail => {
      const createdAt = new Date(detail.created_at);
      return createdAt >= from && createdAt <= to;
    });

    setFilteredData(filtered);
  };

  const handleToggleActive = async (bankDetailId) => {
    try {
      const updatedBankDetail = bankDetails.find(
        (detail) => detail.bank_detail_id === bankDetailId
      );
      const updatedStatus = { ...updatedBankDetail, is_active: !updatedBankDetail.is_active };
      await axios.put(
        `${apiBaseURL}/api/freelancer-withdrawal-bank-details/${bankDetailId}`,
        updatedStatus
      );
      const response = await axios.get(`${apiBaseURL}/api/freelancer-withdrawal-bank-details`);
      setBankDetails(response.data);
      setFilteredData(response.data);
    } catch (err) {
      setError('Failed to update bank detail status.');
    }
  };

  const data = filteredData.map(detail => ({
    bank_detail_id: detail.bank_detail_id,
    freelancer_id: detail.freelancer_id,
    bank_name: detail.bank_name,
    account_number: detail.account_number,
    account_holder_name: detail.account_holder_name,
    ifsc_code: detail.ifsc_code || 'N/A',
    account_type: detail.account_type || 'N/A',
    status: detail.is_active ? 'Active' : 'Inactive',
    cashfree_beneficiary_id: detail.cashfree_beneficiary_id || 'N/A',
    cashfree_name: detail.cashfree_response_account_holder_name || 'N/A',
    cashfree_branch: detail.cashfree_response_branch || 'N/A',
    cashfree_city: detail.cashfree_response_city || 'N/A',
    created_at: detail.created_at ? new Date(detail.created_at).toLocaleString('en-IN') : 'N/A',
    updated_at: detail.updated_at ? new Date(detail.updated_at).toLocaleString('en-IN') : 'N/A'
  }));

  const handleDownloadExcel = () => {
    const now = new Date();

    const formattedDateHeader = now.toLocaleString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

    const formattedFileNameTime = now.toISOString().replace(/T/, '_').replace(/:/g, '-').split('.')[0];
    const fileName = `Freelancer_Withdrawal_Bank_Details_${formattedFileNameTime}.xlsx`;

    const header = [
      ['OrderMinds Freelancing Solutions LLP'],
      ['Freelancer Withdrawal Bank Details'],
      [`Downloaded on: ${formattedDateHeader}`],
      [],
    ];

    const tableHeaders = columns;
    const tableData = data.map(row =>
      columns.map(col => row[col.toLowerCase().replace(/ /g, '_')])
    );

    const worksheetData = [...header, tableHeaders, ...tableData];

    const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Bank Details');

    XLSX.writeFile(workbook, fileName);
  };

  const Table = ({ columns, data }) => (
    <div className="overflow-x-auto">
      <table className="min-w-full table-auto bg-white shadow rounded-lg">
        <thead>
          <tr className="bg-gray-200">
            {columns.map((col, idx) => (
              <th key={idx} className="px-3 py-2 text-left">{col}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-3 text-center text-gray-500">No bank details found.</td>
            </tr>
          ) : (
            data.map((row, rowIdx) => (
              <tr key={rowIdx} className="border-b">
                {columns.map((col, colIdx) => (
                  <td key={colIdx} className="px-3 py-2 whitespace-nowrap">
                    {row[col.toLowerCase().replace(/ /g, '_')]}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  if (loading) return <div className="p-6">Loading bank details...</div>;
  if (error) return <div className="p-6 text-red-500">{error}</div>;

  return (
    <div className="flex-1 p-6 bg-gray-50 ml-64">
      <div className="flex justify-between items-center mb-5">
        <h2 className="text-2xl font-semibold">Freelancer Withdrawal Bank Details</h2>
        <button
          onClick={handleDownloadExcel}
          className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700 transition"
        >
          Download Excel
        </button>
      </div>

      {/* 🔽 Date Range Filter */}
      <div className="mb-4 flex gap-4">
        <div>
          <label className="block text-sm font-medium mb-1">From Date</label>
          <input
            type="date"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            className="border px-3 py-2 rounded w-44"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">To Date</label>
          <input
            type="date"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            className="border px-3 py-2 rounded w-44"
          />
        </div>
        <div className="flex items-end">
          <button
            onClick={handleDateFilter}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 transition"
          >
            Apply Filter
          </button>
        </div>
      </div>

      <Table columns={columns} data={data} />
    </div>
  );
};

export default FreelancerWithdrawalBankDetailsPage;
